/**
 * useMonthlySales — 특정 연도의 1~12월 매출 집계 (홈 KPI 당월매출·평균단가용).
 *
 * sales_invoices 를 거래일 [year-01-01, year-12-31] 범위로 일회성 조회 후
 * client-side 월별 누적. year별 in-memory 캐시 — 같은 year 재요청 시 0 reads.
 *
 * 컨벤션 (PeriodView 와 일치):
 *   - 매출   = Σ total  (= amount + vat, 합계금액. VAT 포함)
 *   - 평균단가 = Σ amount / Σ qty  (공급가/㎥. VAT 제외 — 단가 컬럼 의미와 동일)
 */
import { useEffect, useState } from "react";
import { useDataTenant } from "@/hooks/useTenantPath";
import type { TenantKey } from "@/domain/space";
import { salesRepo } from "@/services/sales.repository";

/** 규격(mixSpec)별 공급가/수량 누적 — 평균단가 규격 필터용. tier(gov/cust) 분리 포함. */
export interface SpecAgg {
  supply: number;
  qty: number;
  govSupply: number;
  govQty: number;
  custSupply: number;
  custQty: number;
}

export interface MonthlySalesBucket {
  /** YYYY-MM */
  ym: string;
  /** 1~12 */
  month: number;
  /** 합계금액 Σ(amount+vat) — 전체 */
  revenue: number;
  /** 공급가액 Σamount — 전체 */
  supply: number;
  /** 수량 Σqty (㎥) — 전체 */
  qtyM3: number;
  /** 평균단가 = supply / qtyM3 (원/㎥). qty 0이면 0 — 전체 */
  avgPrice: number;
  // ── tier 분리 (관급 gov / 사급 cust). 같은 fetch 재집계 — read 증가 0 ──
  /** 관급 합계금액 Σ(amount+vat) */
  govRevenue: number;
  /** 관급 공급가액 Σamount */
  govSupply: number;
  /** 관급 수량 Σqty */
  govQty: number;
  /** 관급 평균단가 = govSupply / govQty */
  govAvgPrice: number;
  /** 사급 합계금액 Σ(amount+vat) */
  custRevenue: number;
  /** 사급 공급가액 Σamount */
  custSupply: number;
  /** 사급 수량 Σqty */
  custQty: number;
  /** 사급 평균단가 = custSupply / custQty */
  custAvgPrice: number;
  /** 규격별 공급가/수량 누적 (key = mixSpec, 예: "25-21-150"). 평균단가 규격 선택용. */
  bySpec: Record<string, SpecAgg>;
}

/**
 * 캐시 키 = "법인:연도" — 법인 전환 시 다른 법인 집계가 섞이지 않게(2026-10-20 1단계).
 * ⚠️ 지금 조회(salesRepo)는 법인 구분 없는 중앙 컬렉션이라 경원 키에도 중앙 숫자가 담긴다(경원 공간은 "데이터 미연결" 안내 띠).
 *    2단계(tenants/{법인}/… 이전)에서 조회에 t 를 넘기면 키는 그대로 쓴다.
 */
const cache = new Map<string, MonthlySalesBucket[]>();

function initBuckets(year: number): MonthlySalesBucket[] {
  const arr: MonthlySalesBucket[] = [];
  for (let m = 1; m <= 12; m++) {
    arr.push({
      ym: `${year}-${String(m).padStart(2, "0")}`,
      month: m,
      revenue: 0,
      supply: 0,
      qtyM3: 0,
      avgPrice: 0,
      govRevenue: 0,
      govSupply: 0,
      govQty: 0,
      govAvgPrice: 0,
      custRevenue: 0,
      custSupply: 0,
      custQty: 0,
      custAvgPrice: 0,
      bySpec: {},
    });
  }
  return arr;
}

export interface UseMonthlySalesState {
  monthly: MonthlySalesBucket[];
  loading: boolean;
  error: string | null;
}

export function useMonthlySales(year: number, tenant?: TenantKey): UseMonthlySalesState {
  const dataTenant = useDataTenant();
  const t = tenant ?? dataTenant;
  const ck = `${t}:${year}`;
  // state 에 법인을 함께 둔다 — 법인이 바뀐 직후(새 조회 전) 이전 법인 숫자를 내보내지 않게
  const [state, setState] = useState<{ t: TenantKey; data: MonthlySalesBucket[] }>(
    () => ({ t, data: cache.get(ck) ?? initBuckets(year) }),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // year <= 0 → 비활성 (권한 게이팅 sentinel). fetch 안 함 (permission-denied 회피).
    if (year <= 0) {
      setState({ t, data: initBuckets(year) });
      return;
    }
    const cached = cache.get(ck);
    if (cached) {
      setState({ t, data: cached });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    salesRepo
      .fetchInvoicesBetween(`${year}-01-01`, `${year}-12-31`)
      .then((rows) => {
        if (cancelled) return;
        const buckets = initBuckets(year);
        for (const r of rows) {
          // date 는 YYYY-MM-DD (거래 달력일)
          const mm = parseInt(r.date.slice(5, 7), 10);
          if (!mm || mm < 1 || mm > 12) continue;
          const b = buckets[mm - 1];
          b.revenue += r.total;
          b.supply += r.amount;
          b.qtyM3 += r.qtyM3;
          if (r.tier === "gov") {
            b.govRevenue += r.total;
            b.govSupply += r.amount;
            b.govQty += r.qtyM3;
          } else {
            b.custRevenue += r.total;
            b.custSupply += r.amount;
            b.custQty += r.qtyM3;
          }
          // 규격별 누적 (평균단가 규격 필터용)
          const spec = r.mixSpec?.trim();
          if (spec) {
            let sa = b.bySpec[spec];
            if (!sa) {
              sa = { supply: 0, qty: 0, govSupply: 0, govQty: 0, custSupply: 0, custQty: 0 };
              b.bySpec[spec] = sa;
            }
            sa.supply += r.amount;
            sa.qty += r.qtyM3;
            if (r.tier === "gov") {
              sa.govSupply += r.amount;
              sa.govQty += r.qtyM3;
            } else {
              sa.custSupply += r.amount;
              sa.custQty += r.qtyM3;
            }
          }
        }
        for (const b of buckets) {
          b.revenue = Math.round(b.revenue);
          b.supply = Math.round(b.supply);
          b.qtyM3 = Math.round(b.qtyM3 * 10) / 10;
          b.avgPrice = b.qtyM3 > 0 ? Math.round(b.supply / b.qtyM3) : 0;
          b.govRevenue = Math.round(b.govRevenue);
          b.govSupply = Math.round(b.govSupply);
          b.govQty = Math.round(b.govQty * 10) / 10;
          b.govAvgPrice = b.govQty > 0 ? Math.round(b.govSupply / b.govQty) : 0;
          b.custRevenue = Math.round(b.custRevenue);
          b.custSupply = Math.round(b.custSupply);
          b.custQty = Math.round(b.custQty * 10) / 10;
          b.custAvgPrice = b.custQty > 0 ? Math.round(b.custSupply / b.custQty) : 0;
        }
        cache.set(ck, buckets);
        setState({ t, data: buckets });
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, ck, t]);

  const monthly = state.t === t ? state.data : initBuckets(year);
  return { monthly, loading, error };
}
